import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Phone, Star, Zap, Clock, ShieldCheck } from "lucide-react";
import { IMAGES, PHONE, PHONE_TEL, waLink } from "../../data";

const LINES = [
  "Elettricista",
  "professionale a",
  "Napoli e provincia",
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.33, 1, 0.68, 1] },
  }),
};

const badges = [
  { icon: Star, label: "4,8/5 recensioni Google" },
  { icon: Zap, label: "Interventi rapidi" },
  { icon: Clock, label: "Disponibile 24 ore su 24" },
  { icon: ShieldCheck, label: "Professionista qualificato" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen overflow-hidden pt-28 sm:pt-32"
      data-testid="hero-section"
    >
      {/* background glow */}
      <div className="pointer-events-none absolute -top-40 right-0 h-[600px] w-[600px] rounded-full bg-[#FFC400]/10 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -left-40 h-[500px] w-[500px] rounded-full bg-[#0B1F3A] blur-[120px]" />

      <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-8">
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#FFC400]/30 bg-[#FFC400]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#FFC400]"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#FFC400]" />
            Pronto intervento 24h · Napoli
          </motion.div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden py-[1px]">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {line === "Napoli e provincia" ? (
                    <span className="text-[#FFC400]">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-xl text-base text-white/70 sm:text-lg"
          >
            Interventi elettrici rapidi, sicuri e risolutivi per abitazioni,
            attività commerciali e manutenzioni.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="flex items-center justify-center gap-2 rounded-full bg-[#FFC400] px-7 py-4 text-base font-bold text-[#0B1F3A] transition-transform duration-200 hover:scale-[1.03] glow-yellow"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={2.5} /> Scrivi su WhatsApp
            </a>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="hero-call-btn"
              className="flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-4 text-base font-bold text-white transition-colors duration-200 hover:border-[#FFC400]/60 hover:text-[#FFC400]"
            >
              <Phone className="h-5 w-5" strokeWidth={2.5} /> Chiama ora
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4"
          >
            {badges.map((b) => (
              <div
                key={b.label}
                className="flex flex-col items-start gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3"
              >
                <b.icon className="h-5 w-5 text-[#FFC400]" strokeWidth={2.2} />
                <span className="text-xs font-medium leading-tight text-white/75">
                  {b.label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Spotlight image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-white/10">
            <motion.img
              src={IMAGES.hero}
              alt="Elettricista ELETTRIKPRO al lavoro su un quadro elettrico a Napoli"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#020813] via-transparent to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-[#FFC400]/10" />
          </div>

          {/* floating rating card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="glass absolute -bottom-5 -left-6 rounded-2xl p-4"
          >
            <div className="flex items-center gap-1 text-[#FFC400]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-[#FFC400]" />
              ))}
            </div>
            <p className="mt-1 text-2xl font-extrabold text-white font-display">
              4,8/5
            </p>
            <p className="text-xs text-white/60">Oltre 25 recensioni</p>
          </motion.div>

          <div className="glass absolute -right-4 top-8 rounded-2xl px-4 py-3">
            <p className="text-xs text-white/60">Chiama</p>
            <p className="font-display font-bold text-[#FFC400]">{PHONE}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
